'use client';

import { useEffect, useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { getCookie, setCookie } from 'cookies-next';
import { Loader } from 'lucide-react';

import { Button } from '../common';
import { Typography } from '../Typography';
import { Notification } from '@/components/common';
import { loginWithMicrosoft } from '@/server-actions';
import { API_URL } from '@/utils/constants';

const MicrosoftLogin = () => {
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  const handleTokenAndRedirect = useCallback(
    (token: string, redirectTo: string = '') => {
      setIsLoading(true);
      setCookie('token', decodeURIComponent(token));
      router.push(redirectTo || '/login');
    },
    [router],
  );

  const checkSession = useCallback(async () => {
    try {
      const token = getCookie('token');
      if (!token) return;
      const result = await fetch(`${API_URL}/user/check_session`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const response = await result.json();

      if (
        response?.data?.is_microsoft_login &&
        response?.data?.is_salesforce_login
      ) {
        router.push('/dashboard');
      }
    } catch (error) {
      console.error('Session check failed:', error);
      Notification('Session check failed', 'error');
    }
  }, [router]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const error = urlParams.get('error');
    const redirectTo = urlParams.get('redirect_to') || '';

    if (error) {
      Notification(error, 'error');
      return;
    }

    if (token) {
      setIsLoading(true);
      handleTokenAndRedirect(token, redirectTo);
    }
  }, [handleTokenAndRedirect]);

  useEffect(() => {
    const token = getCookie('token');
    if (token) {
      checkSession();
    }
  }, [checkSession]);

  const handleMicrosoftLogin = async () => {
    try {
      setIsLoading(true);
      const response = await loginWithMicrosoft();

      if (!response?.data?.ms_url) {
        throw new Error('Microsoft login URL not found');
      }

      window.open(response.data.ms_url, '_self');
    } catch (error) {
      console.error('Microsoft login failed:', error);
      Notification('Microsoft login failed!', 'error');
      setIsLoading(false);
    }
  };

  return (
    <div className="grid h-full items-center justify-items-center p-10">
      <div className="flex w-full flex-col items-center space-y-4">
        <div className="flex justify-center">
          <img
            src="/assets/amarco.png"
            alt="Amarco Logo"
            className="w-3/4 bg-cover 2xl:px-7 2xl:py-2"
          />
        </div>

        <Typography.LabelMediumExtraSmall className="w-4/2 p-5 py-8 text-center text-sm font-bold leading-6 text-white md:text-sm">
          Log in to CrescentGPT here.
        </Typography.LabelMediumExtraSmall>

        <Button
          className="text-xs font-light text-black md:px-8 2xl:py-6"
          onClick={handleMicrosoftLogin}
          isDisabled={isLoading}
        >
          {isLoading ? (
            <Loader className="mx-20 animate-spin" />
          ) : (
            <>
              <img
                src="/assets/microsoft-logo.png"
                alt="Microsoft Logo"
                className="w-5 lg:text-sm"
              />
              Sign in with Microsoft 365
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export { MicrosoftLogin };
